import type { CSSProperties, ReactNode } from "react"
import { EncabezadoEmpresa } from "@/componentes/empresa/EncabezadoEmpresa"
import type { Empresa } from "@/componentes/empresa/tipos"
import { monto } from "@/componentes/tesoreria/dinero"
import { comoFecha } from "@/lib/fechas"
import type { BorradorRetencion, Evaluacion } from "./tipos"
import { lineasValidas, vigente } from "./tipos"

/** Un comprobante por impuesto: el de ISLR y el de IVA se entregan por separado. */
export type TipoComprobante = "ISLR" | "IVA"

export const TIPOS_COMPROBANTE: readonly TipoComprobante[] = ["ISLR", "IVA"]

const NOMBRE: Record<TipoComprobante, string> = { ISLR: "I.S.L.R.", IVA: "I.V.A." }

/**
 * La hoja impresa de UNA retención: la de ISLR o la de IVA.
 *
 * ⚠ **Un comprobante por impuesto, a petición del usuario.** El Excel los juntaba en
 * una hoja; el de ISLR y el de IVA se declaran por separado y cada uno se entrega
 * con su base, su porcentaje y su monto. El mismo documento del tercero produce los
 * dos, con el mismo código y el sufijo del impuesto.
 *
 * ⚠ **Es el MISMO componente en la previa y en el papel**, como en rotulación.
 *
 * ⚠ **No calcula nada.** Las cifras llegan en `calculo`, del servidor —también la base
 * de IVA, `base_iva_sujeta`—. Mientras no han llegado se pinta un guion: imprimir un
 * cero que no es el monto sería peor que imprimir un hueco. La única resta es la
 * columna «a pagar» del formato, exacta en céntimos.
 *
 * ⚠ **Con reajuste, se imprime REAJUSTADO** (pedido del 08/10): las bases, las
 * retenciones y el total a la tasa del pago, y un bloque que deja ver la emisión y la
 * diferencia. Es lo que se transfiere; la emisión queda como referencia.
 *
 * ⚠ **Sin la línea «SetiecaDesk 3.0» del membrete**: es la firma del software, y este
 * papel es del cliente hacia un tercero.
 */
export function ComprobanteRetencion({
  tipo,
  datos,
  calculo,
  codigo,
  empresa,
}: {
  tipo: TipoComprobante
  datos: BorradorRetencion
  calculo: Evaluacion | undefined
  codigo: string | null
  empresa: Empresa | undefined
}) {
  const cifra = (valor: string | undefined) => (valor === undefined ? "—" : monto(valor))
  const resta = (a: string | undefined, b: string | undefined) =>
    a === undefined || b === undefined ? undefined : restar(a, b)
  const esIslr = tipo === "ISLR"
  const v = calculo ? vigente(calculo) : undefined
  const reajuste = calculo?.reajuste ?? null
  const enviadas = lineasValidas(datos.lineas)
  // ⚠ La respuesta puede ser la ANTERIOR mientras se teclea (`keepPreviousData`): si
  // no trae un monto por línea, se pinta un guion antes que el de otra línea.
  const montos = v && v.montos.length === enviadas.length ? v.montos : undefined
  // Sólo los servicios que llevan ESTA retención.
  const lineas = enviadas
    .map((l, i) => ({ ...l, base: montos?.[i] }))
    .filter((l) => (esIslr ? l.retiene_islr : !l.exento_iva && l.retiene_iva))
  const pct = porcentaje(esIslr ? datos.porcentaje_ret_islr : datos.porcentaje_ret_iva)

  return (
    <article className="pieza-impresa" style={HOJA}>
      <EncabezadoEmpresa
        empresa={empresa}
        // Sin la palabra «Borrador» aunque no se haya guardado: se imprime y se entrega
        // así, y en el papel del cliente esa palabra no dice nada (lo pidió el usuario).
        titulo={codigo ? `${codigo} · ${tipo}` : tipo}
        sinEtiquetaSistema={true}
      />

      <h1 style={{ margin: "6mm 0 4mm", textAlign: "center", fontSize: "13pt", letterSpacing: 1 }}>
        COMPROBANTE DE RETENCIÓN PROVISIONAL DE {NOMBRE[tipo]}
      </h1>

      <dl style={DATOS}>
        <Dato
          rotulo="Beneficiario"
          valor={conRif(datos.beneficiario_nombre, datos.beneficiario_rif)}
        />
        <Dato rotulo="Agente de retención" valor={conRif(datos.agente_nombre, datos.agente_rif)} />
        <Dato rotulo="Fecha" valor={comoFecha(datos.fecha)} />
        {datos.documento && <Dato rotulo="Documento" valor={datos.documento} />}
        {datos.bl && <Dato rotulo="B/L" valor={datos.bl} />}
        {datos.tasa_cambio && (
          <Dato rotulo="Tasa de emisión" valor={`${datos.tasa_cambio} Bs/USD`} />
        )}
        {reajuste && (
          <Dato
            rotulo="Tasa de reajuste"
            valor={`${datos.tasa_reajuste} Bs/USD${datos.fecha_reajuste ? ` (${comoFecha(datos.fecha_reajuste)})` : ""}`}
          />
        )}
        {datos.concepto && <Dato rotulo="Concepto" valor={datos.concepto} />}
      </dl>

      <table style={TABLA}>
        <thead>
          <tr>
            <th style={{ ...CELDA, textAlign: "left" }}>Servicio</th>
            <th style={CELDA}>Base Bs</th>
            {!esIslr && <th style={CELDA}>Alícuota</th>}
          </tr>
        </thead>
        <tbody>
          {lineas.length === 0 ? (
            <tr>
              <td style={{ ...CELDA, textAlign: "left" }} colSpan={esIslr ? 2 : 3}>
                Ningún servicio lleva esta retención.
              </td>
            </tr>
          ) : (
            lineas.map((l, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: las líneas no tienen identidad propia
              <tr key={i}>
                <td style={{ ...CELDA, textAlign: "left" }}>{l.descripcion || "Servicio"}</td>
                <td style={CELDA}>{cifra(l.base)}</td>
                {!esIslr && <td style={CELDA}>{porcentaje(datos.alicuota_iva)} %</td>}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {esIslr ? (
        <Bloque titulo={`RETENCIÓN DE I.S.L.R. ${pct} %`}>
          <Cifra rotulo="Base imponible" valor={cifra(v?.calculo.base_islr)} />
          <Cifra rotulo="Monto retenido" valor={cifra(v?.calculo.monto_ret_islr)} fuerte={true} />
          <Cifra
            rotulo="Monto a pagar"
            valor={cifra(resta(v?.calculo.base_islr, v?.calculo.monto_ret_islr))}
          />
        </Bloque>
      ) : (
        <Bloque titulo={`RETENCIÓN DE I.V.A. ${pct} %`} columnas={4}>
          <Cifra rotulo="Base imponible" valor={cifra(v?.calculo.base_iva_sujeta)} />
          <Cifra
            rotulo={`I.V.A. ${porcentaje(datos.alicuota_iva)} %`}
            valor={cifra(v?.calculo.iva_sujeto)}
          />
          <Cifra rotulo="I.V.A. retenido" valor={cifra(v?.calculo.monto_ret_iva)} fuerte={true} />
          <Cifra
            rotulo="I.V.A. a pagar"
            valor={cifra(resta(v?.calculo.iva_sujeto, v?.calculo.monto_ret_iva))}
          />
        </Bloque>
      )}

      <Bloque titulo="TOTAL DEL DOCUMENTO" destacado={true}>
        <Cifra rotulo="Total factura" valor={cifra(v?.calculo.total)} />
        <Cifra rotulo="Total retenciones (ISLR + IVA)" valor={cifra(v?.calculo.total_retenido)} />
        <Cifra rotulo="Total pagado" valor={cifra(v?.calculo.neto)} />
      </Bloque>

      {calculo && reajuste && (
        <Bloque titulo="REAJUSTE CAMBIARIO">
          <Cifra rotulo="Total factura a la tasa de emisión" valor={monto(calculo.total)} />
          <Cifra rotulo="Total factura reajustado" valor={monto(reajuste.calculo.total)} />
          <Cifra rotulo="Diferencia" valor={monto(reajuste.diferencia_total)} />
        </Bloque>
      )}

      {datos.observaciones && (
        <p style={{ margin: "4mm 0 0", fontSize: "9pt" }}>{datos.observaciones}</p>
      )}

      <div style={FIRMA}>
        <div style={{ borderTop: "1px solid var(--impreso-tinta)", paddingTop: "1.5mm" }}>
          {datos.firmante || " "}
        </div>
      </div>
    </article>
  )
}

function Dato({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <>
      <dt style={{ fontWeight: 700 }}>{rotulo.toUpperCase()}:</dt>
      <dd style={{ margin: 0 }}>{valor || " "}</dd>
    </>
  )
}

function Bloque({
  titulo,
  children,
  destacado = false,
  columnas = 3,
}: {
  titulo: string
  children: ReactNode
  destacado?: boolean
  columnas?: number
}) {
  return (
    <section style={{ marginTop: "5mm" }}>
      <h2
        style={{
          margin: "0 0 2mm",
          textAlign: "center",
          fontSize: "10.5pt",
          color: destacado ? "var(--impreso-vino)" : "var(--impreso-tinta)",
        }}
      >
        {titulo}
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${columnas}, 1fr)`, gap: "3mm" }}>
        {children}
      </div>
    </section>
  )
}

function Cifra({
  rotulo,
  valor,
  fuerte = false,
}: {
  rotulo: string
  valor: string
  fuerte?: boolean
}) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: "8.5pt", fontWeight: 700, color: "var(--impreso-gris)" }}>
        {rotulo.toUpperCase()}:
      </div>
      <div
        className="tabular-nums"
        style={{
          marginTop: "1mm",
          padding: "1.5mm 0",
          borderBottom: "1px solid var(--impreso-linea)",
          fontSize: fuerte ? "11.5pt" : "10.5pt",
          fontWeight: fuerte ? 700 : 500,
        }}
      >
        {valor}
      </div>
    </div>
  )
}

function conRif(nombre: string, rif: string): string {
  return rif.trim() ? `${nombre}  ·  RIF ${rif.trim()}` : nombre
}

/** «2.000000» → «2»; «0.75» → «0,75». Sólo para leer: no entra en ninguna cuenta. */
function porcentaje(texto: string): string {
  const n = Number(texto.replace(",", "."))
  return Number.isFinite(n) ? n.toLocaleString("es-VE", { maximumFractionDigits: 4 }) : texto
}

/**
 * La resta de dos montos del servidor, exacta, en céntimos enteros.
 *
 * ⚠ No es un cálculo fiscal —ése lo hace el servidor—: es la columna «a pagar» del
 * formato, que siempre es lo de su izquierda menos lo retenido.
 */
function restar(a: string, b: string): string {
  const centimos = (t: string) => {
    const [entero = "0", fraccion = ""] = t.split(".")
    return BigInt(entero) * 100n + BigInt(`${fraccion}00`.slice(0, 2))
  }
  const r = centimos(a) - centimos(b)
  const negativo = r < 0n
  const abs = negativo ? -r : r
  return `${negativo ? "-" : ""}${abs / 100n}.${(abs % 100n).toString().padStart(2, "0")}`
}

const HOJA: CSSProperties = {
  width: "190mm",
  padding: "8mm 10mm 10mm",
  fontSize: "10pt",
  lineHeight: 1.35,
}

const DATOS: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  columnGap: "4mm",
  rowGap: "1.2mm",
  margin: 0,
}

const TABLA: CSSProperties = {
  width: "100%",
  marginTop: "5mm",
  borderCollapse: "collapse",
  fontSize: "9pt",
  fontVariantNumeric: "tabular-nums",
}

const CELDA: CSSProperties = {
  padding: "1.2mm 1.5mm",
  borderBottom: "1px solid var(--impreso-linea)",
  textAlign: "right",
}

const FIRMA: CSSProperties = {
  display: "flex",
  justifyContent: "center",
  marginTop: "18mm",
  textAlign: "center",
  fontWeight: 700,
}
